import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, Pencil, Trash2, Shield, AlertTriangle, AlertOctagon, Search, Sparkles, DollarSign, Settings } from "lucide-react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "../../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../../components/ui/alert-dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../components/ui/tabs";
import AdminLayout from "./AdminLayout";
import axios from "axios";
import { API } from "../../App";
import { toast } from "sonner";

// Risk labels with descriptions
const riskLabels = {
  "🔴 Высокий риск": [
    { label: "High Risk", desc: "Общий высокий риск — адрес имеет признаки высокорисковой активности" },
    { label: "Suspicious Activity", desc: "Подозрительная активность — обнаружены нетипичные паттерны транзакций" },
    { label: "AML Risk", desc: "Риск нарушения AML-требований — возможное несоответствие требованиям противодействия отмыванию" },
    { label: "Potential Money Laundering", desc: "Возможное отмывание средств — признаки легализации незаконных доходов" },
  ],
  "🕵️ Мошенничество": [
    { label: "Fraud", desc: "Мошенничество — адрес связан с мошеннической деятельностью" },
    { label: "Scam", desc: "Скам — связь с инвестиционными скамами, romance scam и подобными схемами" },
    { label: "Phishing", desc: "Фишинг — адрес использовался в фишинговых атаках" },
    { label: "Ponzi Scheme", desc: "Финансовая пирамида — связь с пирамидальными схемами" },
  ],
  "🧺 Отмывание и маскировка": [
    { label: "Money Laundering", desc: "Отмывание средств — прямые признаки отмывания денег" },
    { label: "Layering", desc: "Запутывание цепочки — многоуровневые транзакции для сокрытия источника" },
    { label: "Mixer / Tumbler", desc: "Использование миксеров — средства прошли через сервисы смешивания" },
    { label: "Chain Hopping", desc: "Переход между блокчейнами — перевод между сетями для сокрытия следов" },
    { label: "Peel Chain", desc: "Дробление средств — разбиение на множество мелких транзакций" },
  ],
  "🚫 Запрещённые источники": [
    { label: "Illicit Source", desc: "Незаконный источник — средства получены из нелегальных источников" },
    { label: "Darknet Market", desc: "Даркнет-маркетплейс — связь с торговыми площадками даркнета" },
    { label: "Stolen Funds", desc: "Украденные средства — адрес содержит украденные активы" },
    { label: "Hacked Exchange", desc: "Взломанная биржа — средства со взломанной криптобиржи" },
  ],
  "🏦 Регуляторные риски": [
    { label: "Sanctioned Entity", desc: "Санкционный адрес — адрес находится в санкционных списках" },
    { label: "OFAC Sanction", desc: "Связь с OFAC — адрес связан с санкциями OFAC" },
    { label: "Terrorism Financing", desc: "Финансирование терроризма — подозрение в финансировании террористической деятельности" },
    { label: "PEP Exposure", desc: "Связь с PEP — связь с политически значимым лицом" },
  ],
  "⚠️ Поведенческие индикаторы": [
    { label: "Unusual Transaction Pattern", desc: "Нетипичное поведение — обнаружены аномальные паттерны транзакций" },
    { label: "Rapid Movement of Funds", desc: "Быстрый прогон средств — средства быстро перемещаются между адресами" },
    { label: "High Velocity", desc: "Высокая скорость оборота — аномально высокая частота транзакций" },
    { label: "Indirect Exposure", desc: "Косвенная связь — непрямая связь с рисковыми адресами через посредников" },
  ],
};

const RiskBadge = ({ level }) => {
  const config = {
    low: { bg: "bg-emerald-50", text: "text-emerald-700", border: "border-emerald-200", icon: Shield },
    medium: { bg: "bg-amber-50", text: "text-amber-700", border: "border-amber-200", icon: AlertTriangle },
    high: { bg: "bg-rose-50", text: "text-rose-700", border: "border-rose-200", icon: AlertOctagon }
  };
  const { bg, text, border, icon: Icon } = config[level] || config.low;
  
  return (
    <Badge className={`${bg} ${text} ${border} border`}>
      <Icon className="w-3 h-3 mr-1" />
      {level}
    </Badge>
  );
};

const networks = [
  "ETH", "BTC", "TRX", "BSC", "POLYGON", "ARBITRUM", "OPTIMISM", "AVAX", "SOL",
  "BASE", "LINEA", "ZKSYNC", "FANTOM", "CRONOS", "HARMONY", "CELO", "MOONBEAM",
  "NEAR", "COSMOS", "POLKADOT", "CARDANO", "ALGORAND", "STELLAR", "RIPPLE",
  "LITECOIN", "DOGECOIN", "TON", "TONCOIN", "APTOS", "SUI", "SEI", "INJECTIVE",
  "MANTLE", "SCROLL", "BLAST", "MODE", "MANTA", "STARKNET", "HEDERA"
];

const AdminAddresses = ({ user }) => {
  const navigate = useNavigate();
  const [addresses, setAddresses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isGenerateDialogOpen, setIsGenerateDialogOpen] = useState(false);
  const [editingAddress, setEditingAddress] = useState(null);
  const [deleteId, setDeleteId] = useState(null);
  const [generating, setGenerating] = useState(false);
  
  const [formData, setFormData] = useState({
    address: "",
    label: "",
    network: "ETH",
    risk_level: "low",
    tags: "",
    notes: "",
    trust_score: 50
  });

  const [generateData, setGenerateData] = useState({
    address: "",
    target_amount: "",
    network: "ETH",
    num_transactions: 15,
    label: ""
  });

  const fetchAddresses = async () => {
    try {
      const response = await axios.get(`${API}/admin/addresses`, { withCredentials: true });
      setAddresses(response.data);
    } catch (error) {
      toast.error("Ошибка загрузки адресов");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAddresses();
  }, []);

  const openCreateDialog = () => {
    setEditingAddress(null);
    setFormData({
      address: "",
      label: "",
      network: "ETH",
      risk_level: "low",
      tags: "",
      notes: ""
    });
    setIsDialogOpen(true);
  };

  const openGenerateDialog = () => {
    setGenerateData({
      address: "",
      target_amount: "",
      network: "ETH",
      num_transactions: 15,
      label: ""
    });
    setIsGenerateDialogOpen(true);
  };

  const openEditDialog = (addr) => {
    setEditingAddress(addr);
    setFormData({
      address: addr.address,
      label: addr.label || "",
      network: addr.network,
      risk_level: addr.risk_level,
      tags: addr.tags?.join(", ") || "",
      notes: addr.notes || "",
      trust_score: addr.trust_score || 50,
      risk_analysis: addr.risk_analysis || ""
    });
    setIsDialogOpen(true);
  };

  const handleSubmit = async () => {
    try {
      const payload = {
        ...formData,
        tags: formData.tags ? formData.tags.split(",").map(t => t.trim()).filter(Boolean) : []
      };

      if (editingAddress) {
        await axios.put(
          `${API}/admin/addresses/${editingAddress.address_id}`,
          payload,
          { withCredentials: true }
        );
        toast.success("Адрес обновлен");
      } else {
        await axios.post(`${API}/admin/addresses`, payload, { withCredentials: true });
        toast.success("Адрес создан");
      }
      
      setIsDialogOpen(false);
      fetchAddresses();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Ошибка сохранения");
    }
  };

  const handleGenerate = async () => {
    if (!generateData.address || !generateData.target_amount) {
      toast.error("Введите адрес и целевую сумму");
      return;
    }

    setGenerating(true);
    try {
      const response = await axios.post(
        `${API}/admin/generate-address`,
        {
          ...generateData,
          target_amount: parseFloat(generateData.target_amount),
          num_transactions: parseInt(generateData.num_transactions)
        },
        { withCredentials: true }
      );
      toast.success(`Сгенерировано ${response.data.trading_history?.length || 0} транзакций`);
      setIsGenerateDialogOpen(false);
      fetchAddresses();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Ошибка генерации");
    } finally {
      setGenerating(false);
    }
  };

  const handleDelete = async () => {
    try {
      await axios.delete(`${API}/admin/addresses/${deleteId}`, { withCredentials: true });
      toast.success("Адрес удален");
      setDeleteId(null);
      fetchAddresses();
    } catch (error) {
      toast.error("Ошибка удаления");
    }
  };

  const filteredAddresses = addresses.filter(addr => 
    addr.address.toLowerCase().includes(searchQuery.toLowerCase()) ||
    addr.label?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    addr.network.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <AdminLayout user={user}>
      <div className="space-y-6" data-testid="admin-addresses">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Адреса</h1>
            <p className="text-slate-500">Управление отслеживаемыми адресами</p>
          </div>
          <div className="flex items-center gap-2">
            <Button 
              onClick={openGenerateDialog} 
              variant="outline"
              className="border-blue-200 text-blue-700 hover:bg-blue-50"
              data-testid="generate-address-btn"
            >
              <Sparkles className="w-4 h-4 mr-2" />
              Авто-генерация
            </Button>
            <Button onClick={openCreateDialog} className="bg-slate-900 hover:bg-slate-800" data-testid="add-address-btn">
              <Plus className="w-4 h-4 mr-2" />
              Добавить вручную
            </Button>
          </div>
        </div>

        {/* Search */}
        <div className="relative max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input
            placeholder="Поиск по адресу, метке или сети..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10"
            data-testid="search-addresses"
          />
        </div>

        {/* List */}
        {loading ? (
          <div className="flex justify-center py-12">
            <div className="w-8 h-8 border-2 border-slate-900 border-t-transparent rounded-full animate-spin"></div>
          </div>
        ) : filteredAddresses.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center text-slate-500">
              {searchQuery ? "Адреса не найдены" : "Нет адресов. Добавьте первый адрес или используйте авто-генерацию."}
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-3">
            {filteredAddresses.map((addr) => (
              <Card key={addr.address_id} className="card-hover" data-testid={`address-${addr.address_id}`}>
                <CardContent className="p-4">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-2">
                        <Badge variant="outline" className="font-mono text-xs">{addr.network}</Badge>
                        <RiskBadge level={addr.risk_level} />
                        {addr.total_balance && (
                          <Badge className="bg-blue-50 text-blue-700 border-blue-200 border">
                            <DollarSign className="w-3 h-3 mr-1" />
                            {addr.total_balance.toLocaleString()}
                          </Badge>
                        )}
                        {addr.tags?.includes("auto-generated") && (
                          <Badge className="bg-purple-50 text-purple-700 border-purple-200 border">
                            <Sparkles className="w-3 h-3 mr-1" />
                            Авто
                          </Badge>
                        )}
                      </div>
                      <p className="font-mono text-sm text-slate-900 break-all mb-1">
                        {addr.address}
                      </p>
                      {addr.label && (
                        <p className="text-sm text-slate-600">{addr.label}</p>
                      )}
                      {addr.trading_history && (
                        <p className="text-xs text-slate-500 mt-1">
                          {addr.trading_history.length} торговых операций
                        </p>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      <Button 
                        variant="outline" 
                        size="sm" 
                        onClick={() => navigate(`/admin/addresses/${addr.address_id}/edit`)}
                        className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                        data-testid={`manage-${addr.address_id}`}
                      >
                        <Settings className="w-4 h-4 mr-1" />
                        Транзакции
                      </Button>
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        onClick={() => openEditDialog(addr)}
                        data-testid={`edit-${addr.address_id}`}
                      >
                        <Pencil className="w-4 h-4" />
                      </Button>
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        onClick={() => setDeleteId(addr.address_id)}
                        className="text-rose-600 hover:text-rose-700 hover:bg-rose-50"
                        data-testid={`delete-${addr.address_id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Create/Edit Dialog */}
        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogContent className="max-w-lg max-h-[90vh] overflow-hidden flex flex-col !grid-rows-none">
            <DialogHeader className="shrink-0">
              <DialogTitle>
                {editingAddress ? "Редактировать адрес" : "Добавить адрес"}
              </DialogTitle>
            </DialogHeader>
            
            <div className="space-y-4 py-2 overflow-y-auto flex-1 min-h-0 pr-1">
              <div className="space-y-2">
                <Label>Адрес кошелька *</Label>
                <Input
                  placeholder="0x..."
                  value={formData.address}
                  onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                  className="font-mono"
                  disabled={!!editingAddress}
                  data-testid="input-address"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Сеть</Label>
                  <Select 
                    value={formData.network} 
                    onValueChange={(v) => setFormData({ ...formData, network: v })}
                  >
                    <SelectTrigger data-testid="select-network">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {networks.map(n => (
                        <SelectItem key={n} value={n}>{n}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>Уровень риска</Label>
                  <Select 
                    value={formData.risk_level} 
                    onValueChange={(v) => setFormData({ ...formData, risk_level: v })}
                  >
                    <SelectTrigger data-testid="select-risk">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="low">
                        <span className="flex items-center gap-2">
                          <Shield className="w-4 h-4 text-emerald-600" />
                          Низкий
                        </span>
                      </SelectItem>
                      <SelectItem value="medium">
                        <span className="flex items-center gap-2">
                          <AlertTriangle className="w-4 h-4 text-amber-600" />
                          Средний
                        </span>
                      </SelectItem>
                      <SelectItem value="high">
                        <span className="flex items-center gap-2">
                          <AlertOctagon className="w-4 h-4 text-rose-600" />
                          Высокий
                        </span>
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Trust Score Slider */}
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label>Рейтинг доверия</Label>
                  <span className={`text-lg font-bold ${
                    formData.trust_score >= 70 ? 'text-emerald-600' :
                    formData.trust_score >= 40 ? 'text-amber-600' : 'text-rose-600'
                  }`}>
                    {formData.trust_score}/100
                  </span>
                </div>
                <input
                  type="range"
                  min="0"
                  max="100"
                  value={formData.trust_score || 50}
                  onChange={(e) => setFormData({ ...formData, trust_score: parseInt(e.target.value) })}
                  className="w-full h-2 bg-slate-200 rounded-lg appearance-none cursor-pointer"
                  style={{
                    background: `linear-gradient(to right, ${
                      formData.trust_score >= 70 ? '#10b981' :
                      formData.trust_score >= 40 ? '#f59e0b' : '#ef4444'
                    } 0%, ${
                      formData.trust_score >= 70 ? '#10b981' :
                      formData.trust_score >= 40 ? '#f59e0b' : '#ef4444'
                    } ${formData.trust_score}%, #e2e8f0 ${formData.trust_score}%, #e2e8f0 100%)`
                  }}
                />
                <div className="flex justify-between text-xs text-slate-500">
                  <span>0 - Опасный</span>
                  <span>50 - Нейтральный</span>
                  <span>100 - Надёжный</span>
                </div>
              </div>

              <div className="space-y-2">
                <Label>Метка</Label>
                <Input
                  placeholder="Например: Binance Hot Wallet"
                  value={formData.label}
                  onChange={(e) => setFormData({ ...formData, label: e.target.value })}
                  data-testid="input-label"
                />
              </div>

              <div className="space-y-2">
                <Label>Теги (через запятую)</Label>
                <Input
                  placeholder="exchange, kyc, verified"
                  value={formData.tags}
                  onChange={(e) => setFormData({ ...formData, tags: e.target.value })}
                  data-testid="input-tags"
                />
              </div>

              <div className="space-y-2">
                <Label>Заметки</Label>
                <Textarea
                  placeholder="Дополнительная информация..."
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  rows={3}
                  data-testid="input-notes"
                />
              </div>

              <div className="space-y-2">
                <Label>Анализ рисков (видно клиенту)</Label>
                
                {/* Active Labels Display - Removable */}
                {formData.risk_analysis && (
                  <div className="border border-rose-200 rounded-lg p-2 bg-rose-50">
                    <div className="flex items-center justify-between mb-2">
                      <p className="text-xs text-rose-600 font-medium">Активные метки (нажмите для удаления):</p>
                      <button
                        type="button"
                        onClick={() => setFormData({ ...formData, risk_analysis: "" })}
                        className="text-xs text-rose-600 hover:text-rose-800 underline"
                      >
                        Очистить всё
                      </button>
                    </div>
                    <div className="flex flex-wrap gap-1">
                      {(formData.risk_analysis.match(/【([^】]+)】/g) || []).map((match, idx) => {
                        const label = match.replace(/[【】]/g, "");
                        return (
                          <button
                            key={idx}
                            type="button"
                            onClick={() => {
                              // Remove this label and its description
                              const regex = new RegExp(`【${label}】[^【]*`, "g");
                              const newText = formData.risk_analysis
                                .replace(regex, "")
                                .replace(/\n{3,}/g, "\n\n")
                                .trim();
                              setFormData({ ...formData, risk_analysis: newText });
                            }}
                            className="text-xs px-2 py-1 bg-rose-100 border border-rose-300 rounded text-rose-700 hover:bg-rose-200 transition-colors flex items-center gap-1"
                          >
                            {label}
                            <span className="text-rose-500">✕</span>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Risk Labels Selector */}
                <div className="border border-slate-200 rounded-lg p-3 bg-slate-50 max-h-48 overflow-y-auto">
                  <p className="text-xs text-slate-500 mb-2">Нажмите для добавления метки:</p>
                  {Object.entries(riskLabels).map(([category, labels]) => (
                    <div key={category} className="mb-2">
                      <p className="text-xs font-semibold text-slate-700 mb-1">{category}</p>
                      <div className="flex flex-wrap gap-1">
                        {labels.map((item) => {
                          const isAdded = formData.risk_analysis?.includes(`【${item.label}】`);
                          return (
                            <button
                              key={item.label}
                              type="button"
                              disabled={isAdded}
                              onClick={() => {
                                const newText = `【${item.label}】${item.desc}`;
                                const current = formData.risk_analysis || "";
                                setFormData({ 
                                  ...formData, 
                                  risk_analysis: current ? `${current}\n\n${newText}` : newText 
                                });
                              }}
                              className={`text-xs px-2 py-1 border rounded transition-colors ${
                                isAdded 
                                  ? "bg-emerald-100 border-emerald-300 text-emerald-700 cursor-not-allowed" 
                                  : "bg-white border-slate-200 hover:bg-slate-100 hover:border-slate-300"
                              }`}
                            >
                              {isAdded ? "✓ " : ""}{item.label}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </div>

                <Textarea
                  placeholder="Подробный анализ рисков адреса..."
                  value={formData.risk_analysis || ""}
                  onChange={(e) => setFormData({ ...formData, risk_analysis: e.target.value })}
                  rows={4}
                  data-testid="input-risk-analysis"
                />
              </div>
            </div>

            <DialogFooter className="shrink-0 pt-2 border-t border-slate-100">
              <Button variant="outline" onClick={() => setIsDialogOpen(false)}>
                Отмена
              </Button>
              <Button 
                onClick={handleSubmit} 
                className="bg-slate-900 hover:bg-slate-800"
                disabled={!formData.address}
                data-testid="save-address-btn"
              >
                {editingAddress ? "Сохранить" : "Создать"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Auto-Generate Dialog */}
        <Dialog open={isGenerateDialogOpen} onOpenChange={setIsGenerateDialogOpen}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Sparkles className="w-5 h-5 text-blue-600" />
                Авто-генерация адреса
              </DialogTitle>
              <DialogDescription>
                Введите адрес и целевую сумму. Система автоматически сгенерирует историю торговых операций.
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label>Адрес кошелька *</Label>
                <Input
                  placeholder="0x1234...abcd"
                  value={generateData.address}
                  onChange={(e) => setGenerateData({ ...generateData, address: e.target.value })}
                  className="font-mono"
                  data-testid="gen-input-address"
                />
              </div>

              <div className="space-y-2">
                <Label>Целевой баланс (USD) *</Label>
                <div className="relative">
                  <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    type="number"
                    placeholder="5000"
                    value={generateData.target_amount}
                    onChange={(e) => setGenerateData({ ...generateData, target_amount: e.target.value })}
                    className="pl-10"
                    data-testid="gen-input-amount"
                  />
                </div>
                <p className="text-xs text-slate-500">
                  Сумма, к которой должна прийти история торговли
                </p>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Сеть</Label>
                  <Select 
                    value={generateData.network} 
                    onValueChange={(v) => setGenerateData({ ...generateData, network: v })}
                  >
                    <SelectTrigger data-testid="gen-select-network">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {networks.map(n => (
                        <SelectItem key={n} value={n}>{n}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>Кол-во транзакций</Label>
                  <Select 
                    value={String(generateData.num_transactions)} 
                    onValueChange={(v) => setGenerateData({ ...generateData, num_transactions: parseInt(v) })}
                  >
                    <SelectTrigger data-testid="gen-select-txcount">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="5">5 транзакций</SelectItem>
                      <SelectItem value="10">10 транзакций</SelectItem>
                      <SelectItem value="15">15 транзакций</SelectItem>
                      <SelectItem value="20">20 транзакций</SelectItem>
                      <SelectItem value="30">30 транзакций</SelectItem>
                      <SelectItem value="50">50 транзакций</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="space-y-2">
                <Label>Метка (опционально)</Label>
                <Input
                  placeholder="Название кошелька"
                  value={generateData.label}
                  onChange={(e) => setGenerateData({ ...generateData, label: e.target.value })}
                  data-testid="gen-input-label"
                />
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <h4 className="font-medium text-blue-900 mb-2">Что будет сгенерировано:</h4>
                <ul className="text-sm text-blue-700 space-y-1">
                  <li>• Покупка/продажа криптовалют (BTC, ETH, SOL...)</li>
                  <li>• Торговля акциями (AAPL, TSLA, NVDA...)</li>
                  <li>• Обмены на DEX (Uniswap, PancakeSwap)</li>
                  <li>• Стейкинг и награды</li>
                  <li>• Переводы между биржами</li>
                </ul>
              </div>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setIsGenerateDialogOpen(false)}>
                Отмена
              </Button>
              <Button 
                onClick={handleGenerate} 
                className="bg-blue-600 hover:bg-blue-700"
                disabled={!generateData.address || !generateData.target_amount || generating}
                data-testid="generate-btn"
              >
                {generating ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                    Генерация...
                  </>
                ) : (
                  <>
                    <Sparkles className="w-4 h-4 mr-2" />
                    Сгенерировать
                  </>
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Delete Confirmation */}
        <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Удалить адрес?</AlertDialogTitle>
              <AlertDialogDescription>
                Это действие нельзя отменить. Все связанные маршруты также будут удалены.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Отмена</AlertDialogCancel>
              <AlertDialogAction 
                onClick={handleDelete}
                className="bg-rose-600 hover:bg-rose-700"
                data-testid="confirm-delete"
              >
                Удалить
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </AdminLayout>
  );
};

export default AdminAddresses;
